'use client'

import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { AdvancedFinanceJob, AdvancedFinanceCarSummary } from '@garage/shared'
import { OPERATING_COST_TYPE_LABELS } from '@garage/shared'
import {
  PageHeader,
  Segmented,
  Button,
  EmptyState,
  Skeleton,
  DataTable,
  Chip,
  WarningIcon,
  type Column,
} from '@/ui'
import { api } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useFormatters } from '@/lib/hooks.js'
import { StatCard } from '@/components/StatCard.js'
import { CashFlowChart } from '@/components/finance/CashFlowChart.js'

/**
 * Advanced P&L overview showing job revenue, other income, job costs,
 * overhead, operating costs, and staff costs to derive net profit.
 *
 * @param embedded - When true, doesn't render its own PageHeader
 */
export function AdvancedOverview({ embedded = false }: { embedded?: boolean } = {}) {
  const formatters = useFormatters()
  const { t, i18n } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  // Read months from URL, default to 12
  const monthsParam = searchParams.get('months')
  const months = monthsParam ? parseInt(monthsParam, 10) : 12

  const { data: report, isLoading, error, refetch } = useQuery({
    queryKey: qk.financeAdvanced(months),
    queryFn: () => api.financeAdvanced(months),
  })

  // For cash flow chart, load the simplified report too
  const { data: simplifiedReport } = useQuery({
    queryKey: qk.finance(months),
    queryFn: () => api.finance(months),
  })

  // Segmented control options
  const monthOptions = [
    { value: '1', label: t('finance.periods.thisMonth') },
    { value: '3', label: t('finance.periods.months', { count: 3 }) },
    { value: '6', label: t('finance.periods.months', { count: 6 }) },
    { value: '12', label: t('finance.periods.months', { count: 12 }) },
    { value: '24', label: t('finance.periods.months', { count: 24 }) },
  ]

  const handleMonthsChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('months', value)
    setSearchParams(newParams)
  }

  if (error) {
    return (
      <div className="space-y-8">
        {!embedded && (
          <PageHeader
            title={t('finance.title')}
            subtitle={t('finance.subtitle')}
          />
        )}
        <div className="flex items-center justify-center py-12">
          <EmptyState
            title={t('finance.failedToLoad')}
            description={t('common.pleaseTryAgain')}
            action={
              <Button variant="outlined" onClick={() => refetch()}>
                {t('common.retry')}
              </Button>
            }
          />
        </div>
      </div>
    )
  }

  if (isLoading || !report) {
    return (
      <div className="space-y-8">
        {!embedded && (
          <PageHeader
            title={t('finance.title')}
            subtitle={t('finance.subtitle')}
          />
        )}
        <div className="flex items-center justify-between">
          <Segmented
            options={monthOptions}
            value={String(months)}
            onChange={handleMonthsChange}
            label={t('finance.timePeriod')}
            className="no-scrollbar"
          />
        </div>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="bg-surface-container border border-outline-variant rounded-lg p-5"
            >
              <Skeleton lines={3} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const { summary, series, jobs, byCar, operatingByType } = report

  // P&L monthly table columns
  const plColumns: Column<typeof series[0]>[] = [
    {
      key: 'period',
      header: t('finance.columns.month'),
      cell: (row) => {
        const parts = row.period.split('-')
        if (parts.length < 2 || !parts[0] || !parts[1]) return <div>{row.period}</div>
        const date = new Date(parseInt(parts[0]!), parseInt(parts[1]!) - 1, 1)
        const locale = i18n.language === 'ar' ? 'ar-u-nu-latn' : 'en-US'
        return <div>{date.toLocaleDateString(locale, { month: 'short', year: 'numeric' })}</div>
      },
      width: 'w-24',
    },
    {
      key: 'revenueMinor',
      header: t('accounting.advanced.columns.revenue'),
      cell: (row) => <div className="tabular">{formatters.amount(row.revenueMinor)}</div>,
      numeric: true,
    },
    {
      key: 'jobCostMinor',
      header: t('accounting.advanced.columns.jobCosts'),
      cell: (row) => <div className="tabular">{formatters.amount(row.jobCostMinor)}</div>,
      numeric: true,
    },
    {
      key: 'grossProfitMinor',
      header: t('accounting.advanced.columns.grossProfit'),
      cell: (row) => <div className="tabular">{formatters.amount(row.grossProfitMinor)}</div>,
      numeric: true,
    },
    {
      key: 'supplierOverheadMinor',
      header: t('accounting.advanced.columns.overhead'),
      cell: (row) => <div className="tabular">{formatters.amount(row.supplierOverheadMinor)}</div>,
      numeric: true,
    },
    {
      key: 'operatingCostMinor',
      header: t('accounting.advanced.columns.operatingCosts'),
      cell: (row) => <div className="tabular">{formatters.amount(row.operatingCostMinor)}</div>,
      numeric: true,
    },
    {
      key: 'staffCostMinor',
      header: t('accounting.advanced.columns.staffCosts'),
      cell: (row) => <div className="tabular">{formatters.amount(row.staffCostMinor)}</div>,
      numeric: true,
    },
    {
      key: 'paymentFeesMinor',
      header: t('accounting.advanced.columns.paymentFees'),
      cell: (row) => <div className="tabular">{formatters.amount(row.paymentFeesMinor)}</div>,
      numeric: true,
    },
    {
      key: 'netProfitMinor',
      header: t('accounting.advanced.columns.netProfit'),
      cell: (row) => <div className="tabular font-medium">{formatters.amount(row.netProfitMinor)}</div>,
      numeric: true,
    },
  ]

  // Jobs table columns
  const jobsColumns: Column<AdvancedFinanceJob>[] = [
    {
      key: 'number',
      header: t('dashboard.columns.number'),
      cell: (row) => (
        <a href={`/jobcards/${row.jobcardId}`} className="text-primary hover:underline ref">
          {row.number}
        </a>
      ),
    },
    {
      key: 'carLabel',
      header: t('dashboard.columns.car'),
      cell: (row) => <div>{row.carLabel}</div>,
    },
    {
      key: 'paidAt',
      header: t('calendar.date'),
      cell: (row) => <div>{formatters.date(row.paidAt)}</div>,
    },
    {
      key: 'revenueMinor',
      header: t('accounting.advanced.columns.revenue'),
      cell: (row) => <div className="tabular">{formatters.amount(row.revenueMinor)}</div>,
      numeric: true,
    },
    {
      key: 'costMinor',
      header: t('accounting.advanced.columns.cost'),
      cell: (row) => <div className="tabular">{formatters.amount(row.costMinor)}</div>,
      numeric: true,
    },
    {
      key: 'paymentFeeMinor',
      header: t('accounting.advanced.columns.paymentFees'),
      cell: (row) => <div className="tabular">{formatters.amount(row.paymentFeeMinor)}</div>,
      numeric: true,
    },
    {
      key: 'profitMinor',
      header: t('accounting.advanced.columns.profit'),
      cell: (row) => <div className="tabular font-medium">{formatters.amount(row.profitMinor)}</div>,
      numeric: true,
    },
    {
      key: 'costSource',
      header: t('accounting.advanced.columns.costSource'),
      cell: (row) => {
        const labels: Record<typeof row.costSource, string> = {
          invoices: t('accounting.advanced.costSources.invoices'),
          estimate: t('accounting.advanced.costSources.estimate'),
          none: t('accounting.advanced.costSources.none'),
        }
        return (
          <div className="space-y-2">
            <Chip label={labels[row.costSource]} />
            {row.invoicesBelowEstimate && (
              <div className="text-body-xs text-warning bg-warning-container rounded px-2 py-1">
                {t('accounting.advanced.invoicesBelowEstimate')}
              </div>
            )}
          </div>
        )
      },
    },
  ]

  // Cars table columns
  const carsColumns: Column<AdvancedFinanceCarSummary>[] = [
    {
      key: 'carLabel',
      header: t('dashboard.columns.car'),
      cell: (row) => <div>{row.carLabel}</div>,
    },
    {
      key: 'jobCount',
      header: t('accounting.advanced.columns.jobCount'),
      cell: (row) => <div className="tabular">{row.jobCount}</div>,
      numeric: true,
    },
    {
      key: 'revenueMinor',
      header: t('accounting.advanced.columns.revenue'),
      cell: (row) => <div className="tabular">{formatters.amount(row.revenueMinor)}</div>,
      numeric: true,
    },
    {
      key: 'costMinor',
      header: t('accounting.advanced.columns.cost'),
      cell: (row) => <div className="tabular">{formatters.amount(row.costMinor)}</div>,
      numeric: true,
    },
    {
      key: 'profitMinor',
      header: t('accounting.advanced.columns.profit'),
      cell: (row) => <div className="tabular font-medium">{formatters.amount(row.profitMinor)}</div>,
      numeric: true,
    },
  ]

  return (
    <div className="space-y-8">
      {!embedded && (
        <PageHeader
          title={t('finance.title')}
          subtitle={t('finance.subtitle')}
        />
      )}

      {/* Time period control */}
      <div className="flex items-center justify-between">
        <Segmented
          options={monthOptions}
          value={String(months)}
          onChange={handleMonthsChange}
          label={t('finance.timePeriod')}
          className="no-scrollbar"
        />
      </div>

      {/* Stat cards: P&L summary */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          label={t('accounting.advanced.cards.totalRevenue')}
          value={formatters.money(summary.revenueMinor)}
        />
        <StatCard
          label={t('accounting.advanced.cards.jobCosts')}
          value={formatters.money(summary.jobCostMinor)}
        />
        <StatCard
          label={t('accounting.advanced.cards.grossProfit')}
          value={formatters.money(summary.grossProfitMinor)}
        />
        <StatCard
          label={t('accounting.advanced.cards.overheads')}
          value={formatters.money(
            summary.supplierOverheadMinor + summary.operatingCostMinor + summary.staffCostMinor + summary.paymentFeesMinor
          )}
          breakdown={t('accounting.advanced.cards.overheadsBreakdown', {
            supplier: formatters.money(summary.supplierOverheadMinor),
            operating: formatters.money(summary.operatingCostMinor),
            staff: formatters.money(summary.staffCostMinor),
            fees: formatters.money(summary.paymentFeesMinor),
          })}
        />
        <StatCard
          label={t('accounting.advanced.cards.netProfit')}
          value={formatters.money(summary.netProfitMinor)}
        />
        <StatCard
          label={t('accounting.advanced.cards.netMargin')}
          value={summary.netMarginPct.toFixed(1) + '%'}
        />
      </div>

      {/* Cash-side figures (from simplified report) */}
      {simplifiedReport && (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
          <StatCard
            label={t('finance.columns.cashReceived')}
            value={formatters.money(simplifiedReport.summary.cashReceivedMinor)}
            meta={t('finance.paidCount', { count: simplifiedReport.summary.jobcardCount })}
          />
          <StatCard
            label={t('finance.outstanding')}
            value={formatters.money(simplifiedReport.summary.outstandingMinor)}
            breakdown={t('finance.outstandingBreakdown', { count: simplifiedReport.summary.outstandingCount })}
          />
        </div>
      )}

      {/* WIP cost warning */}
      {summary.wipCostMinor > 0 && (
        <div className="bg-surface-container border border-outline-variant rounded-lg p-5 flex gap-3">
          <WarningIcon className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
          <div className="text-body-sm text-on-surface">
            {t('accounting.advanced.wipWarning', { amount: formatters.money(summary.wipCostMinor) })}
          </div>
        </div>
      )}

      {/* Monthly P&L table */}
      <section className="space-y-4">
        <h2 className="text-title-md text-on-surface">{t('accounting.advanced.monthlyPL')}</h2>
        <DataTable
          columns={plColumns}
          rows={series}
          rowKey={(row) => row.period}
          stackOnMobile
          empty={
            <EmptyState
              title={t('finance.noData')}
              description={t('finance.noDataDescription')}
            />
          }
        />
        <p className="text-body-xs text-on-surface-variant italic">
          {t('accounting.advanced.staffCostNote')}
        </p>
        <p className="text-body-xs text-on-surface-variant italic">
          {t('accounting.advanced.netBasisNote')}
        </p>
      </section>

      {/* Operating costs by type */}
      {operatingByType.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-title-md text-on-surface">{t('accounting.advanced.overheadsBreakdown')}</h2>
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
            {operatingByType.map((item) => {
              const typeLabel = t(`accounting.advanced.costTypes.${item.type}`, OPERATING_COST_TYPE_LABELS[item.type])
              return (
                <div key={item.type} className="bg-surface-container border border-outline-variant rounded-lg p-4 text-center">
                  <div className="text-body-sm text-on-surface-variant mb-2">{typeLabel}</div>
                  <div className="text-title-md text-on-surface">{formatters.money(item.amountMinor)}</div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Payment fees by method */}
      {report.paymentFeesByMethod.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-title-md text-on-surface">{t('accounting.advanced.paymentFeesByMethod')}</h2>
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
            {report.paymentFeesByMethod.map((item) => {
              const methodLabel = t(`paymentMethods.${item.method}`)
              return (
                <div key={item.method} className="bg-surface-container border border-outline-variant rounded-lg p-4 text-center">
                  <div className="text-body-sm text-on-surface-variant mb-2">{methodLabel}</div>
                  <div className="text-title-md text-on-surface">{formatters.money(item.feeMinor)}</div>
                  <div className="text-body-xs text-on-surface-variant mt-2">{item.jobCount} job{item.jobCount !== 1 ? 's' : ''}</div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Cash flow (from simplified report) */}
      {simplifiedReport && (
        <section className="space-y-4">
          <h2 className="text-title-md text-on-surface">{t('finance.cashFlow')}</h2>
          {simplifiedReport.series.length === 0 || simplifiedReport.series.every((s) => s.cashReceivedMinor === 0) ? (
            <EmptyState
              title={t('finance.noCashReceived')}
              description={t('finance.noCashReceivedDescription')}
            />
          ) : (
            <div className="bg-surface-container border border-outline-variant rounded-lg p-5 flex flex-col">
              <div className="flex-1 min-h-64">
                <CashFlowChart series={simplifiedReport.series} />
              </div>
            </div>
          )}
        </section>
      )}

      {/* Profit by job */}
      {jobs.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-title-md text-on-surface">{t('accounting.advanced.profitByJob')}</h2>
          <DataTable
            columns={jobsColumns}
            rows={jobs}
            rowKey={(row) => row.jobcardId}
            stackOnMobile
          />
        </section>
      )}

      {/* Profit by car */}
      {byCar.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-title-md text-on-surface">{t('accounting.advanced.profitByCar')}</h2>
          <DataTable
            columns={carsColumns}
            rows={byCar}
            rowKey={(row) => row.carId ?? row.carLabel}
            stackOnMobile
          />
        </section>
      )}
    </div>
  )
}
